import React from 'react';
import PropTypes from 'prop-types';
import Pressable from "react-native-web/dist/exports/Pressable";
import Platform from "react-native-web/dist/exports/Platform";
import View from "react-native-web/dist/exports/View";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import { useViewport } from '../ViewportProvider';
import { applyOuterBorder, validateThemeTokens } from '../ThemeProvider';
import { a11yProps, clickProps, focusHandlerProps, getTokensSetPropType, linkProps, resolvePressableState, resolvePressableTokens, selectSystemProps, selectTokens, variantProp, viewProps, withLinkRouter } from '../utils';
import CardBase from './CardBase';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, focusHandlerProps, viewProps]);
const tokenKeys = ['flex', 'backgroundColor', 'borderColor', 'gradient', 'borderRadius', 'borderWidth', 'paddingBottom', 'paddingLeft', 'paddingRight', 'paddingTop', 'minWidth', 'shadow', 'contentAlignItems', 'contentJustifyContent', 'contentFlexGrow', 'contentFlexShrink',
// Outer border tokens. TODO: centralise common token sets like these as part of
// https://github.com/telus/universal-design-system/issues/782
'outerBorderColor', 'outerBorderWidth', 'outerBorderGap', 'icon', 'iconBackgroundColor', 'iconColor', 'iconSize', 'inputBackgroundColor', 'inputBorderColor', 'inputBorderRadius', 'inputBorderWidth', 'inputHeight', 'inputShadow', 'inputWidth'];
export const selectPressableCardTokens = tokens => Object.fromEntries(tokenKeys.map(key => [key, tokens[key]]));

/**
 * A pressable themeless base component that handles pressable states and passes tokens
 * based on these to an outer border and a base Card component. Not intended to be used in
 * apps or sites directly: build themed components on top of this.
 */
const PressableCardBase = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    children,
    tokens,
    checked,
    validation,
    inactive,
    href,
    hrefAttrs,
    dataSet,
    accessibilityRole = href ? 'link' : undefined,
    ...rawRest
  } = _ref;
  const {
    onPress,
    ...rest
  } = clickProps.toPressProps(rawRest);
  const viewport = useViewport();
  const additionalState = {
    checked,
    validation,
    inactive,
    viewport
  };
  const getCardState = pressableState => resolvePressableState(pressableState, additionalState);
  const getTokens = pressableState => validateThemeTokens(resolvePressableTokens(tokens, pressableState, additionalState), getTokensSetPropType(tokenKeys, {
    partial: true,
    allowFunction: true
  }), 'PressableCard');
  const getCardTokens = pressableState => selectTokens('Card', getTokens(pressableState));
  const getOuterBorderStyle = pressableState => {
    const {
      flex,
      minWidth,
      outerBorderColor,
      outerBorderGap = 0,
      outerBorderWidth = 0,
      borderRadius
    } = getTokens(pressableState);
    return {
      flex,
      minWidth: minWidth + outerBorderGap + outerBorderWidth,
      ...applyOuterBorder({
        outerBorderColor,
        outerBorderGap,
        outerBorderWidth,
        borderRadius
      }),
      ...Platform.select({
        web: {
          outline: 'none'
        }
      })
    };
  };
  const handleChange = linkProps.handleHref({
    href,
    onPress
  });
  const handleKeyDown = event => {
    // The expected keyboard shortcut for selecting a focused item is the Space key
    if (event?.key === ' ') handleChange?.(event);
  };
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  if (Platform.OS === 'web' && href) {
    return /*#__PURE__*/_jsx(View, {
      ref: ref,
      style: getOuterBorderStyle({
        pressed,
        focused,
        hovered
      }),
      dataSet: dataSet,
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
      ...selectProps({
        ...rest,
        accessibilityRole
      }),
      children: /*#__PURE__*/_jsx("a", {
        href: href,
        tabIndex: 0,
        onClick: event => {
          setPressed(false);
          handleChange?.(event);
        },
        onKeyDown: event => {
          if (event.key === ' ') {
            setPressed(true);
            handleKeyDown(event);
          }
        },
        onKeyUp: event => {
          if (event.key === ' ') setPressed(false);
        },
        onMouseDown: () => setPressed(true),
        onMouseUp: () => setPressed(false),
        onFocus: () => setFocused(true),
        onBlur: () => {
          setFocused(false);
          setPressed(false);
        },
        style: {
          ...staticStyles.container,
          textDecoration: 'none'
        },
        ...(hrefAttrs || {}),
        children: /*#__PURE__*/_jsx(CardBase, {
          tokens: getCardTokens({
            pressed,
            focused,
            hovered
          }),
          children: typeof children === 'function' ? children(getCardState({
            pressed,
            focused,
            hovered
          })) : children
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(Pressable, {
    ref: ref,
    href: href,
    onPress: handleChange,
    onKeyDown: handleKeyDown,
    hrefAttrs: hrefAttrs,
    style: getOuterBorderStyle,
    dataSet: dataSet,
    ...selectProps({
      ...rest,
      accessibilityRole
    }),
    children: pressableState => /*#__PURE__*/_jsx(CardBase, {
      tokens: getCardTokens(pressableState),
      children: typeof children === 'function' ? children(getCardState(pressableState)) : children
    })
  });
});
const staticStyles = StyleSheet.create({
  container: {
    flex: 1,
    display: 'flex'
  }
});
PressableCardBase.displayName = 'PressableCardBase';
PressableCardBase.propTypes = {
  ...selectedSystemPropTypes,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  tokens: getTokensSetPropType(tokenKeys, {
    partial: true,
    allowFunction: true
  }),
  variant: variantProp.propType
};
export default withLinkRouter(PressableCardBase);